import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import SectionHeading from '../components/SectionHeading';
import PhoneMockup from '../components/PhoneMockup';

const slides = [
  {
    src: '/screenshots/empathease-nvc-course-learning-path.webp',
    alt: 'EmpathEase NVC course with structured learning path and lessons',
    caption: 'Structured NVC Learning Path',
    description: 'Master empathic communication through bite-sized lessons',
  },
  {
    src: '/screenshots/empathease-live-empathy-sessions.webp',
    alt: 'EmpathEase live peer empathy sessions with give and receive options',
    caption: 'Live Empathy Sessions',
    description: 'Practice with real people — give or receive empathy',
  },
  {
    src: '/screenshots/empathease-ai-tools-overview.webp',
    alt: 'EmpathEase AI tools including Giraffe Translator and Needs Guesser',
    caption: 'AI-Powered NVC Tools',
    description: 'Transform your communication with intelligent coaching',
  },
  {
    src: '/screenshots/empathease-giraffe-translator-tool.webp',
    alt: 'EmpathEase Giraffe Translator tool for reframing messages empathically',
    caption: 'Giraffe Translator',
    description: 'Reframe any message into empathic, needs-based language',
  },
  {
    src: '/screenshots/empathease-daily-goals-progress-tracking.webp',
    alt: 'EmpathEase daily goals and progress tracking dashboard',
    caption: 'Daily & Weekly Goals',
    description: 'Stay on track with achievable daily practice goals',
  },
  {
    src: '/screenshots/empathease-stats-empathy-impact.webp',
    alt: 'EmpathEase stats showing empathy impact and community contributions',
    caption: 'Track Your Empathy Impact',
    description: 'See how your practice ripples through the community',
  },
  {
    src: '/screenshots/empathease-communication-games.webp',
    alt: 'EmpathEase communication games including Pitfall Swipe',
    caption: 'Learn Through Play',
    description: 'Fun games that sharpen your communication instincts',
  },
  {
    src: '/screenshots/empathease-pitfall-swipe-game.webp',
    alt: 'EmpathEase Pitfall Swipe game for identifying communication pitfalls',
    caption: 'Pitfall Swipe',
    description: 'Swipe to spot the difference between empathy and judgment',
  },
  {
    src: '/screenshots/empathease-needs-guesser-tool.webp',
    alt: 'EmpathEase Needs Guesser tool using Max-Neef universal needs framework',
    caption: 'Needs Guesser',
    description: 'Discover the universal needs behind any experience',
  },
  {
    src: '/screenshots/empathease-community-leaderboard.webp',
    alt: 'EmpathEase community leaderboard with points and achievement rankings',
    caption: 'Community Leaderboard',
    description: 'Grow alongside a supportive community of learners',
  },
  {
    src: '/screenshots/empathease-user-profile.webp',
    alt: 'EmpathEase user profile showing empathy impact statistics',
    caption: 'Your Profile & Stats',
    description: 'Track sessions given, time contributed, and your impact',
  },
];

const AUTO_ADVANCE_MS = 4000;
const SWIPE_THRESHOLD = 50;

export default function AppShowcaseSection() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const userInteracted = useRef(false);
  const touchStartX = useRef(0);

  const markInteracted = useCallback(() => {
    userInteracted.current = true;
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (index === current) return;
      markInteracted();
      setDirection(index > current ? 1 : -1);
      setCurrent(index);
    },
    [markInteracted, current],
  );

  const goNext = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % slides.length);
  }, []);

  const goPrev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(goNext, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [paused, goNext]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        markInteracted();
        goPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        markInteracted();
        goNext();
      }
    },
    [goNext, goPrev, markInteracted],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      markInteracted();
      setPaused(true);
    },
    [markInteracted],
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      if (delta < -SWIPE_THRESHOLD) goNext();
      else if (delta > SWIPE_THRESHOLD) goPrev();
      setPaused(false);
    },
    [goNext, goPrev],
  );

  const slide = slides[current];

  return (
    // Why: hover pauses the auto-advancing carousel. Mouse-only is acceptable
    // for this enhancement — keyboard users get a fully accessible carousel
    // via the role="region" + arrow-key handlers on the inner container below.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <section
      id="app-showcase"
      className="ee-landing-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="ee-landing-container ee-landing-container--mid">
        <SectionHeading
          title="See It In Action"
          subtitle="A complete platform for learning empathic communication — right in your pocket."
        />

        {/* Why: arrow-key navigation over a labelled carousel region, which
            needs the region focusable to receive the keys. It is an ADDITION
            to the Previous/Next buttons below, not a replacement — remove it
            and keyboard users still have the buttons, they just lose arrows.
            A block pair rather than -next-line: the two rules report on
            different lines (the element, and the tabIndex attribute). */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          className="ee-landing-show"
          role="region"
          aria-roledescription="carousel"
          aria-label="App screenshots"
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
          {/* Phone mockup */}
          <div
            className="ee-landing-show__stage"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="ee-landing-show__phone">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, x: 30 * direction }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 * direction }}
                  transition={{ duration: 0.3 }}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${current + 1} of ${slides.length}: ${slide.caption}`}
                >
                  <PhoneMockup src={slide.src} alt={slide.alt} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Nav arrows - flanking the phone */}
            <button
              type="button"
              onClick={() => {
                markInteracted();
                goPrev();
              }}
              className="ee-landing-show__arrow ee-landing-show__arrow--prev"
              aria-label="Previous screenshot"
            >
              <LuChevronLeft aria-hidden="true" focusable="false" />
            </button>
            <button
              type="button"
              onClick={() => {
                markInteracted();
                goNext();
              }}
              className="ee-landing-show__arrow ee-landing-show__arrow--next"
              aria-label="Next screenshot"
            >
              <LuChevronRight aria-hidden="true" focusable="false" />
            </button>
          </div>

          {/* Caption & dots */}
          <div className="ee-landing-show__caption">
            <div aria-live={userInteracted.current ? 'polite' : 'off'} aria-atomic="true">
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  <h3 className="ee-landing-show__title">{slide.caption}</h3>
                  <p className="ee-landing-show__text">{slide.description}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Dot navigation */}
            <div className="ee-landing-show__dots" aria-label="Slide navigation">
              {slides.map((s, i) => (
                <button
                  key={s.caption}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-current={i === current ? 'true' : undefined}
                  className="ee-landing-show__dot"
                  aria-label={`Go to ${s.caption}`}
                >
                  <span className="ee-landing-show__dot-mark" />
                </button>
              ))}
            </div>

            {/* Slide counter */}
            <p className="ee-landing-show__count">
              {current + 1} of {slides.length}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
